import { generateBenefitSummaries, getIncludedBenefits } from 'api/operations/proposalServiceV3';
import { makeObservable, observable, computed, action, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import IUWProductsPage from 'store/modules/uwProducts/iUWProductsPage';
import UWProductsModule, { AttributeInfo } from 'store/modules/uwProducts/uwProductsModule';
import { RootStore, getRootStoreInstance } from 'store/rootStore';
import { InputGenerateProductTemplate, ProductTemplate, ProductTemplateChecklistCard } from 'types/productServiceV2';
import { COMMON_URLS } from '@insurancemenu/imenu-components-lib/dist/utils/urls';

export interface GeneratePdfRequiredInfo extends AttributeInfo {
  /**
   * Selection status
   */
  status: boolean;
}

export default class GeneratePDFPageStore extends IUWProductsPage {
  rootStore: RootStore;
  uwProductsModule: UWProductsModule;

  private _isLoading = false;
  private _isCompleteStepLoading = false;
  private _sponsorId = '';
  private _productTemplate: ProductTemplate | null = null;
  private _requiredAttributes: AttributeInfo[] = [];
  private _planAttributesMap: Record<string, GeneratePdfRequiredInfo> = {};
  private _includedBenefits: Record<string, boolean> | null = null;
  private _isPdfGenerated = false;

  // Getters
  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get isCompleteStepLoading(): boolean {
    return this._isCompleteStepLoading;
  }

  protected get productTemplate(): ProductTemplate | null {
    return this._productTemplate;
  }

  private get sponsorId(): string {
    return this._sponsorId;
  }

  public get planAttributesMap(): Record<string, GeneratePdfRequiredInfo> {
    return this._planAttributesMap;
  }

  public get requiredAttributes(): AttributeInfo[] {
    return this._requiredAttributes;
  }

  public get isPdfGenerated(): boolean {
    return this._isPdfGenerated;
  }

  public get includedBenefits(): Record<string, boolean> | null {
    return this._includedBenefits;
  }

  // Setters
  private setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  private setIsCompleteStepLoading(status: boolean): void {
    this._isCompleteStepLoading = status;
  }

  private setPlanAttributesMap(data: Record<string, GeneratePdfRequiredInfo>): void {
    this._planAttributesMap = data;
  }

  private setRequiredAttributes(attributesInfo: AttributeInfo[]): void {
    this._requiredAttributes = attributesInfo;
  }

  private setSponsorId(id: string): void {
    this._sponsorId = id;
  }

  private setIncludedBenefits(includedBenefits: Record<string, boolean> | null): void {
    this._includedBenefits = includedBenefits;
  }

  private setProductTemplate(template: ProductTemplate | null): void {
    this._productTemplate = template;
  }

  public setIsPdfGenerated(status: boolean): void {
    this._isPdfGenerated = status;
  }

  // Api calls
  public async getInitialData(templateId: string): Promise<void> {
    this.setIsLoading(true);
    await this.uwProductsModule.getInitialData(templateId);
    await runInAction(async () => {
      this.setProductTemplate(this.uwProductsModule.productTemplate);
      const res = await getIncludedBenefits(templateId);
      runInAction(() => {
        const includedBenefits = res.data?.getIncludedBenefits || null;
        this.setIncludedBenefits(includedBenefits);
        this.setIsPdfGenerated(!!includedBenefits);
        this.setRequiredAttributes(this.uwProductsModule.requiredAttributes);

        const planAttributesMap = this.convertAttributeNamesToMap();
        this.setPlanAttributesMap(planAttributesMap);
        this.setSponsorId(this.rootStore.userStore.sponsorId);

        this.setIsLoading(false);
      });
    });
  }

  public async completeStep(): Promise<void> {
    this.setIsCompleteStepLoading(true);
    await this.uwProductsModule.onCompleteStep(ProductTemplateChecklistCard.GENERATE_PDF);
    this.setIsCompleteStepLoading(false);
  }

  public async generatePdf(): Promise<string> {
    if (!this.uwProductsModule.productTemplate) return '';
    this.setIsLoading(true);
    const { id, name, productLineId, planSummary, formAnswers, maxWaiverMessage } =
      this.uwProductsModule.productTemplate;

    const templateInput: InputGenerateProductTemplate = {
      id,
      name,
      productLineId,
      planSummary,
      formAnswers,
      maxWaiverMessage,
    };

    const attributesStatuses: Record<string, boolean> = {};
    Object.keys(this.planAttributesMap).forEach((attributeName) => {
      attributesStatuses[attributeName] = this.planAttributesMap[attributeName].status;
    });
    const res = await generateBenefitSummaries(
      this._sponsorId,
      templateInput,
      attributesStatuses,
      this.uwProductsModule.catalogId,
    );
    this.setIsLoading(false);
    const downloadURL = res.data?.generateBenefitSummaries || '';
    if (downloadURL) this.setIsPdfGenerated(true);
    return downloadURL;
  }

  // Other
  public resetInitialData(): void {
    this.uwProductsModule.resetInitialData();
    this.setPlanAttributesMap({});
    this.setIncludedBenefits(null);
  }

  public toggleAttributeSelection(attributeName: string): void {
    const planAttributesMap = { ...this.planAttributesMap };
    planAttributesMap[attributeName] = {
      ...planAttributesMap[attributeName],
      status: !planAttributesMap[attributeName].status,
    };
    this.setPlanAttributesMap(planAttributesMap);
  }

  // eslint-disable-next-line class-methods-use-this
  private convertAttributeNamesToMap(): Record<string, GeneratePdfRequiredInfo> {
    const planAttributesMap: Record<string, GeneratePdfRequiredInfo> = {};

    this.requiredAttributes.forEach((attribute) => {
      planAttributesMap[attribute.name] = this.includedBenefits
        ? { ...attribute, status: this.includedBenefits[attribute.name] }
        : // set filled covered attribute selected  by default
          { ...attribute, status: attribute.type === 'FILLED_COVERED' };
    });
    return planAttributesMap;
  }

  public get isStepCompleted(): boolean {
    return this.uwProductsModule.isStepCompleted(ProductTemplateChecklistCard.GENERATE_PDF);
  }

  public get isTemplateInProgress(): boolean {
    return this.uwProductsModule.isTemplateInProgress;
  }

  public get downloadLink(): string {
    if (this.productTemplate) {
      const { id, name } = this.productTemplate;
      return `https://${COMMON_URLS.api}/proposalV3/Prod/download?sponsorId=${
        this.sponsorId
      }&templateId=${id}&templateName=${encodeURIComponent(name)}`;
    }
    return '';
  }

  public get totalSelectedAttributesForPdf(): number {
    return Object.values(this.planAttributesMap).reduce((count, value) => count + (value.status ? 1 : 0), 0);
  }

  constructor() {
    super();
    this.uwProductsModule = new UWProductsModule();
    this.rootStore = getRootStoreInstance();
    makeObservable<
      GeneratePDFPageStore,
      // Private observable properties and actions
      | '_isLoading'
      | '_isCompleteStepLoading'
      | '_planAttributesMap'
      | '_sponsorId'
      | '_productTemplate'
      | '_isPdfGenerated'
      | '_includedBenefits'
      | 'setIsLoading'
      | 'setIsCompleteStepLoading'
      | 'setPlanAttributesMap'
      | 'setRequiredAttributes'
      | 'setSponsorId'
      | 'setProductTemplate'
      | 'setIsPdfGenerated'
      | 'setIncludedBenefits'
    >(this, {
      // Private properties and actions
      _isLoading: observable,
      _isCompleteStepLoading: observable,
      _planAttributesMap: observable,
      _sponsorId: observable,
      _productTemplate: observable,
      _isPdfGenerated: observable,
      _includedBenefits: observable,
      setIsLoading: action.bound,
      setIsCompleteStepLoading: action.bound,
      setPlanAttributesMap: action.bound,
      setRequiredAttributes: action.bound,
      setSponsorId: action.bound,
      setProductTemplate: action.bound,
      setIsPdfGenerated: action.bound,
      setIncludedBenefits: action.bound,

      // Getters
      planAttributesMap: computed,
      requiredAttributes: computed,
      isLoading: computed,
      isCompleteStepLoading: computed,
      isStepCompleted: computed,
      isPdfGenerated: computed,
      totalSelectedAttributesForPdf: computed,
      includedBenefits: computed,

      // Other actions
      getInitialData: action.bound,
      completeStep: action.bound,
      resetInitialData: action.bound,
      toggleAttributeSelection: action.bound,
      generatePdf: action.bound,
    });
  }
}

const PageStoreContext = createContext(new GeneratePDFPageStore());

export const useGeneratePDFStore = (): GeneratePDFPageStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
